import { Trans } from "@lingui/react/macro";
import type { ArtifactMedia } from "@rakazo/core";
import type { ReactNode } from "react";
import { useState } from "react";
import { artifactMediaUrl } from "../lib/artifact-open";

/**
 * Native inline playback streamed from the artifact media route in byte ranges, so seeking works
 * and a long file is never downloaded whole. A file the browser cannot decode renders `fallback`.
 * Callers key it by artifact so a new file starts without the previous failure.
 */
export function ArtifactMediaPlayer({
  artifactId,
  name,
  media,
  preload,
  className,
  fallback,
}: {
  artifactId: string;
  name: string;
  media: ArtifactMedia;
  preload: "none" | "metadata";
  className?: string;
  fallback: ReactNode;
}) {
  const [failed, setFailed] = useState(false);
  if (failed) return fallback;
  const src = artifactMediaUrl(artifactId);
  if (media.kind === "audio") {
    return (
      // biome-ignore lint/a11y/useMediaCaption: Attachment playback has no caption track supplied by the author.
      <audio
        src={src}
        controls
        preload={preload}
        aria-label={name}
        onError={() => setFailed(true)}
        className={className}
      />
    );
  }
  return (
    // biome-ignore lint/a11y/useMediaCaption: Attachment playback has no caption track supplied by the author.
    <video
      // The fragment makes iOS paint the first frame instead of a black box before playback.
      src={`${src}#t=0.001`}
      controls
      playsInline
      preload={preload}
      aria-label={name}
      onError={() => setFailed(true)}
      className={className}
    />
  );
}

/** A whole preview pane for one audio or video artifact, with the download hint as fallback. */
export function ArtifactMediaPreview({
  artifactId,
  name,
  media,
}: {
  artifactId: string;
  name: string;
  media: ArtifactMedia;
}) {
  if (media.kind === "audio") {
    return (
      <div className="grid h-full place-items-center bg-muted/40 p-6">
        <ArtifactMediaPlayer
          key={artifactId}
          artifactId={artifactId}
          name={name}
          media={media}
          preload="metadata"
          className="w-full max-w-xl"
          fallback={<PreviewUnavailable />}
        />
      </div>
    );
  }
  return (
    <ArtifactMediaPlayer
      key={artifactId}
      artifactId={artifactId}
      name={name}
      media={media}
      preload="metadata"
      className="h-full w-full object-contain"
      fallback={<PreviewUnavailable />}
    />
  );
}

function PreviewUnavailable() {
  return (
    <div className="grid h-full place-items-center px-6 text-center text-sm text-muted-foreground/80">
      <Trans>Preview isn't available for this file type — download it to view it.</Trans>
    </div>
  );
}
